// Client Context to manage client data across the app
import React, { createContext, useContext, useState, useEffect } from 'react';

type ClientContextProps =
  | {
      clientName: string | null;
      setClientName: (name: string) => void;
      clientId: string | null;
      setClientId: (id: string) => void;
      activeSessionId: string | null;
      setActiveSessionId: (id: string) => void;
    }
  | undefined;

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

interface ClientProviderProps {
  children: React.ReactNode;
}

export const ClientProvider = ({ children }: ClientProviderProps) => {
  const [clientName, setClientName] = useState<string | null>(sessionStorage.getItem('clientName') || null);
  const [clientId, setClientId] = useState<string | null>(sessionStorage.getItem('clientId') || null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(
    sessionStorage.getItem('activeSessionId') || null,
  );

  useEffect(() => {
    if (clientName) {
      sessionStorage.setItem('clientName', clientName);
    }
  }, [clientName]);

  useEffect(() => {
    if (clientId) {
      sessionStorage.setItem('clientId', clientId);
    }
  }, [clientId]);

  useEffect(() => {
    if (activeSessionId) {
      sessionStorage.setItem('activeSessionId', activeSessionId);
    } else {
      sessionStorage.removeItem('activeSessionId');
    }
  }, [activeSessionId]);

  const resetActiveSessionId = () => {
    setActiveSessionId(null);
  };

  return (
    <ClientContext.Provider
      value={{
        clientName,
        setClientName,
        clientId,
        setClientId,
        activeSessionId,
        setActiveSessionId,
      }}>
      {children}
    </ClientContext.Provider>
  );
};

export function useClientContext() {
  const context = useContext(ClientContext);

  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}
